//===----------------------------------------------------------------------===//
//
//  Copyright 2024-2026 Trovares Inc. dba Rocketgraph.  All rights reserved.
//
//===----------------------------------------------------------------------===//

import { readCookie } from '@/utils/cookies';
import { type ApiClient, type XgtConnectionInfo } from './apiClient';
import { type AppConfig, type AuthUser, type LoginResponse } from './apiTypes';

export interface OidcLoginResponse {
  login: boolean;
  identity: string;
  host: string;
  port: string;
  error?: string;
}

/** Response from the /api/protected session-restore endpoint. */
interface ProtectedResponse {
  user: AuthUser;
  authType: string;
  host: string;
  port: number;
}

/**
 * Authentication and session lifecycle: login/logout, OIDC exchange, session
 * restore, the app-config bootstrap (which gates on restore), and the
 * session-state accessors consumers read.  Owns the auth API over the shared
 * `ApiClient` core, which holds the actual session state + transport; the
 * accessors here are thin pass-throughs to that core.
 */
export class AuthService {
  constructor(private readonly api: ApiClient) {}

  // User login method.
  login = async (
    username: string,
    password: string,
    host: string,
    port: number,
    authType: string,
    sslServerCert: string | null,
    sslClientCert: string | null,
    sslClientKey: string | null
  ): Promise<LoginResponse> => {
    try {
      const response = await this.api.makeRequest<LoginResponse>(
        'login', 'post',
        { 'username':username, 'password': password, 'host': host, 'port': port,
          'authType': authType, 'sslServerCert': sslServerCert,
          'sslClientCert': sslClientCert, 'sslClientKey': sslClientKey }
      );

      if (!response.identity && !username)
        throw new Error('No username or identity.  Login failed.');
      if (response && response.login === true) {
        // Don't login here, since we do other stuff like get the profile
        // etc to make sure we have everything first before logging in.
        // Use completeLogin instead.
        return response;
      } else {
        throw new Error('Login failed.');
      }
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  completeLogin = (
    username: string,
    response: LoginResponse,
    host: string,
    port: number,
    authType: string
  ): void => {
    this.api.setRestoreSessionEnabled(true);
    if (response.identity) {
      this.api.setUser(response.identity);
    } else if (username) {
      this.api.setUser(username);
      response.identity = username;
    }
    this.api.setAuthType(authType);

    // The user is fully logged in and loaded at this point.  Without this, a
    // login right after a logout (which set userLoaded=false) leaves
    // ProtectedRoute gating on isUserLoaded()===false on its first render —
    // and the async restoreSession that would flip it does not trigger a
    // re-render — so /dashboard renders blank until a browser refresh.
    this.api.userLoaded = true;

    // On login, set the xgt connection info on the core, which then becomes
    // available to the context provider.  The context provider then updates its
    // state to force a rerender and update the screen.
    this.api.setXGTConnection({host:host, port:port});

    // No need to manually save cookies as they should be automatically
    // handled by the browser.
  };

  // User logout method.
  logout = async (): Promise<void> => {
    try {
      await this.api.makeRequest('logout', 'post');
    } catch (error) {
      console.error('Logout error:', error);
      throw error;
    } finally {
      // Always clear local session state, even if the logout request failed
      // (network error, or a session already expired server-side) — otherwise
      // the core keeps a stale token while React state thinks we're logged out.
      this.api.setUser(null);
      this.api.userLoaded = false;
      this.api.setAuthType(null);
      this.api.setRestoreSessionEnabled(true);
    }
  };

  // Reset the backend inactivity timer for genuine user activity.  Called by
  // the idle-timeout controller (silent near-expiry extension and the "stay
  // signed in" modal action).  Carries no background-poll header, so it counts
  // as activity server-side.
  keepalive = async (): Promise<void> => {
    await this.api.makeRequest('keepalive', 'post');
  };

  exchangeOidcSession = (oidcSession: string): Promise<OidcLoginResponse> => {
    return this.api.makeRequest<OidcLoginResponse>(
      'login/oidc/session', 'post', { oidc_session: oidcSession });
  };

  getOidcAuthorizationUrl = (
    host: string,
    port: number | string
  ): Promise<{url: string}> => {
    const url = `login/oidc?host=${encodeURIComponent(host)}` +
                `&port=${encodeURIComponent(port)}`;
    return this.api.makeRequest<{url: string}>(url, 'get');
  };

  restoreSession = async (): Promise<void> => {
    // Restore session info if the user is undefined and there is an auth cookie.
    const hasAuthCookie = readCookie('csrf_access_token');

    if (this.api.user === null && hasAuthCookie) {
      // makeRequest throws on session-expiry; let it propagate (which, as today,
      // skips the userLoaded flag below so a failed restore isn't "loaded").
      const response = await this.api.makeRequest<ProtectedResponse>(
        'protected', 'get', null, true);
      if (response?.user !== undefined) {
        this.api.setUser(response.user);
        this.api.setAuthType(response.authType);
        this.api.setXGTConnection({host:response.host, port:response.port});
      }
    }

    // Indicate the user is loaded.
    this.api.userLoaded = true;
  };

  // Checks if there is a current user and if the token is expired.
  isAuthenticated = (): boolean => {
    // The session hasn't been retrieved yet, so return not authenticated.
    if (!this.api.userLoaded)
      return false;

    try {
      // Proper authentication check - ensure user exists and has required data.
      const userExists = Boolean(this.api.user);

      return userExists;
    } catch(e) {
      console.error('[AuthService.isAuthenticated] Error checking auth:', e);
      return false;
    }
  };

  getAppConfig = async (): Promise<AppConfig> => {
    if (this.api.restoreSessionEnabled) {
      // Make sure the session is loaded.
      await this.restoreSession();
    }
    // Return the TD app config.
    return this.api.makeRequest<AppConfig>('config/app', 'get');
  };

  // Session-state accessors — thin pass-throughs to the core that consumers
  // read (AuthProvider's context value, AppBootstrap, DatasetProvider).
  getUser = (): AuthUser | null => this.api.getUser();

  getAuthType = (): string | null => this.api.getAuthType();

  getSessionExpiredTime = (): number | null => this.api.getSessionExpiredTime();

  setRestoreSessionEnabled = (enabled: boolean): void =>
    this.api.setRestoreSessionEnabled(enabled);

  clearSessionExpired = (): void => this.api.clearSessionExpired();

  markSessionExpired = (): void => this.api.markSessionExpired();

  setXGTConnection = (xgtCnxInfo: XgtConnectionInfo): void =>
    this.api.setXGTConnection(xgtCnxInfo);

  getXgtConnectionInfo = (): XgtConnectionInfo => this.api.xgtConnectionInfo;
}
